import React, { useEffect, useState } from 'react';
import { useNavPillsIndicator } from 'hooks/useNavPillsIndicator';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import * as XLSX from 'xlsx';
import LoadingSpinner from 'component/LoadingSpinner';
import CustomDropdown from 'component/CustomDropdown';
import ProjectService from 'service/ProjectService';
import { ProjectDetailResp } from 'domain/payload/response/project/ProjectDetailResp';
import { JobCreationConfigData, JobCreationInquiryData, JobCreationPreviewItem } from 'domain/payload/response/project/ProjectJobCreationResp';
import { JobCreationInquiryPayload } from 'domain/payload/request/project/ProjectJobCreationReq';
import 'bootstrap/dist/css/bootstrap.min.css';

const ProjectJobCreation: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const { profile } = useSelector((state: any) => state.auth);
  const projectService = ProjectService();
  const navigate = useNavigate();

  const [project, setProject] = useState<ProjectDetailResp | null>(null);
  const [config, setConfig] = useState<JobCreationConfigData | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [step, setStep] = useState<1 | 2 | 3>(1);

  // Step 1 State: Config & File Upload
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [isFileParsing, setIsFileParsing] = useState<boolean>(false);
  const [excelHeaders, setExcelHeaders] = useState<string[]>([]);

  const [excelRowCount, setExcelRowCount] = useState<number>(0);
  const [locationType, setLocationType] = useState<string>('place');
  const [placeReferenceType, setPlaceReferenceType] = useState<string>('');
  const [refTypeMode, setRefTypeMode] = useState<'existing' | 'project_name' | 'custom'>('project_name');

  const [customReferenceType, setCustomReferenceType] = useState<string>('');
  const [jobType, setJobType] = useState<number>(1);

  const [lockSubmitDistance, setLockSubmitDistance] = useState<boolean>(true);
  const [requireAudioRecording, setRequireAudioRecording] = useState<boolean>(false);
  const [webFormEligible, setWebFormEligible] = useState<boolean>(false);
  const [editableLocation, setEditableLocation] = useState<boolean>(false);


  const [useDefaultMaxInput, setUseDefaultMaxInput] = useState<boolean>(true);
  const [jobPrefixName, setJobPrefixName] = useState<string>('');


  // Step 2 State: Column Mapping
  const [jobMapping, setJobMapping] = useState({
    name: '',
    description: '',
    formId: '',
    maxInput: '1',
    groupName: '',
    additionalInfo: [] as string[]
  });



  const [placeColumn, setPlaceColumn] = useState('');
  const [placeMapping, setPlaceMapping] = useState({
    name: '',
    address: '',
    phone: '',
    code: '',
    referenceId: '',
    latitude: '',
    longitude: '',
    contactPerson: ''
  });

  // Step 3 State: Inquiry & Preview
  const [isInquiryLoading, setIsInquiryLoading] = useState(false);
  const [inquiryResult, setInquiryResult] = useState<JobCreationInquiryData | null>(null);
  const [isExecuting, setIsExecuting] = useState(false);
  const [isExecutedSuccess, setIsExecutedSuccess] = useState(false);

  // Preview filtering & pagination
  const [filter, setFilter] = useState<'all' | 'valid' | 'invalid'>('all');
  const { navRef: refTypeNavRef, indicatorStyle: refTypeIndicatorStyle } = useNavPillsIndicator(refTypeMode);
  const { navRef: filterNavRef, indicatorStyle: filterIndicatorStyle } = useNavPillsIndicator(filter);
  const [search, setSearch] = useState('');
  const [filteredRows, setFilteredRows] = useState<JobCreationPreviewItem[]>([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  // Fetch initial project detail and job creation config
  useEffect(() => {
    const fetchData = async () => {
      const token = profile?.data?.token;
      if (!token || !id) return;

      try {
        setIsLoading(true);
        const [projectRes, configRes] = await Promise.all([
          projectService.projectDetail(token, id),
          projectService.getJobCreationConfig(token, id)
        ]);

        const projData = projectRes.data || null;
        setProject(projData);
        if (projData?.name) {
          setPlaceReferenceType(projData.name);
        }

        if (configRes.data) {
          const cfg = configRes.data;
          setConfig(cfg);
          if (cfg.locationTypes?.length > 0) {
            const firstLoc = cfg.locationTypes[0];
            setLocationType(typeof firstLoc === 'string' ? firstLoc : firstLoc.code);
          }
          if (cfg.jobTypes?.length > 0) {
            setJobType(cfg.jobTypes[0].code);
          }
          if (cfg.defaultForm?.id) {
            setJobMapping((prev) => ({
              ...prev,
              formId: cfg.defaultForm!.id
            }));
          } else if (cfg.forms?.length > 0) {
            const defaultFormId = cfg.forms[0].id;
            setJobMapping((prev) => ({
              ...prev,
              formId: defaultFormId
            }));
          }
        }






      } catch (err: any) {
        console.error('Error fetching configuration:', err);
        alert(`Error: ${err?.message || 'Gagal memuat konfigurasi job creation'}`);
      } finally {
        setIsLoading(false);
      }
    };

    fetchData();
  }, [id, profile]);

  // Handle Excel file upload & parse headers
  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setSelectedFile(file);
    setIsFileParsing(true);

    setTimeout(() => {
      const reader = new FileReader();

      reader.onload = (event) => {
        try {
          const data = new Uint8Array(event.target?.result as ArrayBuffer);
          const workbook = XLSX.read(data, { type: 'array' });
          const firstSheetName = workbook.SheetNames[0];
          const worksheet = workbook.Sheets[firstSheetName];
          const rows = XLSX.utils.sheet_to_json<any[]>(worksheet, { header: 1 });

          if (!rows || rows.length === 0) {
            alert('File Excel kosong!');
            return;
          }

          // Filter out empty rows (rows where all cells are null, undefined, or empty strings)
          const nonEmptyRows = rows.filter((row) => {
            if (!row || !Array.isArray(row) || row.length === 0) return false;
            return row.some((cell) => String(cell ?? '').trim() !== '');
          });

          if (nonEmptyRows.length === 0) {
            alert('File Excel tidak berisi data!');
            return;
          }

          const headers = (nonEmptyRows[0] || []).map((h: any) => String(h || '').trim()).filter(Boolean);
          setExcelHeaders(headers);
          setExcelRowCount(Math.max(0, nonEmptyRows.length - 1));

          autoMatchHeaders(headers);

        } catch (err) {
          console.error('Failed to parse Excel file:', err);
          alert('Gagal membaca file Excel. Pastikan format file benar (.xlsx / .xls)');
        } finally {
          setIsFileParsing(false);
        }
      };

      reader.onerror = () => {
        setIsFileParsing(false);
        alert('Gagal membaca file Excel');
      };

      reader.readAsArrayBuffer(file);
    }, 50);
  };


  // Helper function to auto-match headers with mapping fields
  const autoMatchHeaders = (headers: string[]) => {
    const findMatch = (candidates: string[]) => {
      return headers.find(h =>
        candidates.some(c => h.toLowerCase() === c.toLowerCase() || h.toLowerCase().includes(c.toLowerCase()))
      ) || '';
    };

    const matchedJobName = findMatch(['Nama Pekerjaan', 'Job Name', 'Pekerjaan']);
    const matchedJobDesc = findMatch(['Deskripsi Pekerjaan', 'Description', 'Deskripsi']);
    const matchedFormName = findMatch(['Tipe Formulir', 'Form Name', 'Form']);
    const matchedMaxInput = findMatch(['Maksimal Input', 'Max Input', 'Max']);
    const matchedGroupName = findMatch(['Nama Grup', 'Group Name', 'Grup', 'Group']);

    setJobMapping((prev) => ({
      ...prev,
      name: matchedJobName,
      description: matchedJobDesc,
      maxInput: matchedMaxInput || prev.maxInput || '1',
      groupName: matchedGroupName
    }));


    const matchedRefId = findMatch(['ID Referensi', 'Reference ID', 'Reference', 'Kode Outlet', 'Ref ID']);
    const matchedPlaceName = findMatch(['Nama Lokasi', 'Place Name', 'Nama Toko', 'Nama Outlet', 'Location Name']);
    const matchedAddress = findMatch(['Alamat Lengkap', 'Alamat', 'Address']);
    const matchedPhone = findMatch(['Nomor Telepon', 'No Telepon', 'Phone', 'Telepon', 'Hp']);
    const matchedCode = findMatch(['Kode Pos', 'Postal Code', 'Zip Code']);
    const matchedLat = findMatch(['Latitude', 'Lat']);
    const matchedLng = findMatch(['Longitude', 'Lng', 'Long']);
    const matchedPic = findMatch(['Nama PIC', 'PIC', 'Contact Person', 'Kontak']);

    setPlaceColumn(matchedRefId || headers[0] || '');
    setPlaceMapping({
      referenceId: matchedRefId,
      name: matchedPlaceName,
      address: matchedAddress,
      phone: matchedPhone,
      code: matchedCode,
      latitude: matchedLat,
      longitude: matchedLng,
      contactPerson: matchedPic
    });
  };

  const handleStep1Next = () => {
    if (!selectedFile) {
      alert('Silakan pilih file Excel terlebih dahulu');
      return;
    }
    if (excelHeaders.length === 0) {
      alert('File Excel tidak memiliki kolom header yang valid');
      return;
    }
    setStep(2);
  };

  const handleAdditionalInfoToggle = (header: string) => {
    setJobMapping((prev) => {
      const exists = prev.additionalInfo.includes(header);
      return {
        ...prev,
        additionalInfo: exists
          ? prev.additionalInfo.filter((item) => item !== header)
          : [...prev.additionalInfo, header]
      };
    });
  };

  // Submit Inquiry & proceed to Step 3
  const handleInquirySubmit = async () => {
    const token = profile?.data?.token;
    if (!token || !id || !selectedFile) return;

    if (!jobMapping.name) {
      alert('Kolom Name pada Job Json Mapping wajib dipilih');
      return;
    }
    if (!placeMapping.referenceId) {
      alert('Kolom Reference ID pada Place Json Mapping wajib dipilih');
      return;
    }
    if (!placeMapping.name) {
      alert('Kolom Name pada Place Json Mapping wajib dipilih');
      return;
    }

    const payload: JobCreationInquiryPayload = {
      projectId: id,
      jobJsonMapping: {
        ...jobMapping,
        prefix: jobPrefixName
      },
      placeJsonMapping: placeMapping,
      jobType,
      requestConfig: {
        lockSubmitDistance,
        requireAudioRecording,
        webFormEligible,
        editableLocation,
        locationType
      },
      placeColumn: placeMapping.referenceId,
      placeReferenceType
    };



    try {
      setIsInquiryLoading(true);
      const res = await projectService.jobCreationInquiry(token, id, selectedFile, payload);
      const data = res.data || null;
      setInquiryResult(data);
      setFilteredRows(data?.preview || []);
      setFilter('all');
      setSearch('');
      setStep(3);
      setCurrentPage(1);
    } catch (err: any) {
      console.error('Inquiry error:', err);
      alert(`Gagal memproses inquiry: ${err || 'Terjadi kesalahan'}`);
    } finally {
      setIsInquiryLoading(false);
    }
  };

  // Execute Job Creation
  const handleExecuteSubmit = async () => {
    const token = profile?.data?.token;
    if (!token || !inquiryResult?.inquiryId) return;

    const confirmExecute = window.confirm(
      `Apakah Anda yakin ingin memproses ${inquiryResult.validRows} pekerjaan valid?`
    );
    if (!confirmExecute) return;

    try {
      setIsExecuting(true);
      await projectService.jobCreationExecute(token, inquiryResult.inquiryId);
      setIsExecutedSuccess(true);
    } catch (err: any) {
      console.error('Execute error:', err);
      alert(`Gagal mengeksekusi job creation: ${err || 'Terjadi kesalahan'}`);
    } finally {
      setIsExecuting(false);
    }
  };

  // Filter preview rows when filter or search changes
  useEffect(() => {
    if (!inquiryResult?.preview) return;

    let list = [...inquiryResult.preview];

    if (filter === 'valid') {
      list = list.filter((r) => r.isValid);
    } else if (filter === 'invalid') {
      list = list.filter((r) => !r.isValid);
    }

    if (search.trim()) {
      const q = search.toLowerCase();
      list = list.filter(
        (r) =>
          r.jobName?.toLowerCase().includes(q) ||
          r.formName?.toLowerCase().includes(q) ||
          r.locationIdentifier?.toLowerCase().includes(q) ||
          r.groupName?.toLowerCase().includes(q) ||
          r.reason?.toLowerCase().includes(q)
      );
    }

    setFilteredRows(list);
    setCurrentPage(1);
  }, [inquiryResult, filter, search]);

  const indexOfLastRow = currentPage * rowsPerPage;
  const indexOfFirstRow = indexOfLastRow - rowsPerPage;
  const currentRows = filteredRows.slice(indexOfFirstRow, indexOfLastRow);
  const totalPages = Math.max(1, Math.ceil(filteredRows.length / rowsPerPage));

  if (isLoading) {
    return (
      <div className="p-5 d-flex justify-content-center align-items-center">
        <LoadingSpinner />
      </div>
    );
  }

  return (
    <div className="pt-1 h-100" style={{ background: '#f2f4f8', minHeight: '100vh' }}>
      <div className="m-4">
        {/* Main Header */}
        <h4 className="mb-4 fw-bold">Project Detail - Bulky Job Creation</h4>

        {/* Main White Card Container */}
        <div className="p-4 bg-white rounded shadow-sm">
          {/* Project Info Header */}
          <div className="mb-4">
            <p className="fw-semibold mb-1">Project Name</p>
            <p className="text-secondary">{project?.name || '-'}</p>
          </div>

          {/* Stepper Bar (Identical to Advance Job Assignment) */}
          <div
            className="d-flex justify-content-between align-items-center mb-5 mx-auto"
            style={{ maxWidth: '700px', position: 'relative' }}
          >
            {['Configure & Upload', 'Column Mapping', 'Preview & Execute'].map((label, index) => {
              const stepNumber = index + 1;
              const isActive = stepNumber === step;
              return (
                <React.Fragment key={label}>
                  <div
                    className="d-flex flex-column align-items-center text-center position-relative"
                    style={{ flex: 1 }}
                  >
                    {index > 0 && (
                      <div
                        className="stepper-line-active"
                        style={{
                          position: 'absolute',
                          left: '-50%',
                          top: '20px',
                          height: '2px',
                          width: '100%',
                          backgroundColor: '#0d6efd',
                          zIndex: 0
                        }}
                      ></div>
                    )}
                    <div
                      className={`d-flex align-items-center justify-content-center fw-bold mb-2 ${isActive ? 'stepper-circle-active' : 'border border-primary text-primary bg-white'
                        }`}
                      style={{
                        width: '40px',
                        height: '40px',
                        borderRadius: '50%',
                        fontSize: '14px',
                        zIndex: 1
                      }}
                    >
                      {stepNumber}
                    </div>
                    <span className={`fw-semibold small ${isActive ? 'text-primary' : 'text-muted'}`}>
                      {label}
                    </span>
                  </div>
                </React.Fragment>
              );
            })}
          </div>

          {/* STEP 1: Configure & Upload */}
          {step === 1 && (
            <>
              <div className="row mb-4">
                <div className="col-md-6 mb-3">
                  <label className="fw-semibold mb-2">Location Type</label>
                  <CustomDropdown
                    value={locationType}
                    options={(config?.locationTypes || []).map((loc: any) => {
                      const code = typeof loc === 'string' ? loc : loc.code;
                      const label = typeof loc === 'string' ? loc : (loc.label || loc.code);
                      return { id: code, name: label };
                    })}
                    onChange={(option) => setLocationType(String(option.id))}
                    placeholder="Select Location Type"
                    containerClassName="w-100"
                    buttonClassName="form-control w-100 d-flex align-items-center justify-content-between text-start bg-white text-dark border rounded-3"
                    buttonStyle={{ height: '50px' }}
                  />
                </div>

                <div className="col-md-6 mb-3">
                  <div className="d-flex justify-content-between align-items-center mb-2">
                    <label className="fw-semibold mb-0">Place Reference Type</label>
                    <div ref={refTypeNavRef} className="nav-pills-animated">
                      <div className="nav-pill-indicator" style={refTypeIndicatorStyle} />
                      <button
                        type="button"
                        className={`nav-link btn-sm ${refTypeMode === 'project_name' ? 'active' : ''}`}
                        onClick={() => {
                          setRefTypeMode('project_name');
                          setPlaceReferenceType(project?.name || '');
                        }}
                      >
                        Use project name
                      </button>
                      <button
                        type="button"
                        className={`nav-link btn-sm ${refTypeMode === 'existing' ? 'active' : ''}`}
                        onClick={() => {
                          setRefTypeMode('existing');
                          if (config && config.referenceTypes && config.referenceTypes.length > 0) {
                            setPlaceReferenceType(config.referenceTypes[0]);
                          }
                        }}
                      >
                        Find reference existing
                      </button>
                      <button
                        type="button"
                        className={`nav-link btn-sm ${refTypeMode === 'custom' ? 'active' : ''}`}
                        onClick={() => {
                          setRefTypeMode('custom');
                          setPlaceReferenceType(customReferenceType);
                        }}
                      >
                        Custom
                      </button>
                    </div>
                  </div>


                  {refTypeMode === 'existing' && (
                    <CustomDropdown
                      value={placeReferenceType}
                      options={(config?.referenceTypes || []).map((ref) => ({ id: ref, name: ref }))}
                      onChange={(option) => setPlaceReferenceType(String(option.id))}
                      placeholder="Select Reference Type"
                      containerClassName="w-100"
                      buttonClassName="form-control w-100 d-flex align-items-center justify-content-between text-start bg-white text-dark border rounded-3"
                      buttonStyle={{ height: '50px' }}
                    />
                  )}

                  {refTypeMode === 'project_name' && (
                    <input
                      type="text"
                      className="form-control bg-light"
                      value={project?.name || ''}
                      readOnly
                      style={{ height: '50px' }}
                    />
                  )}

                  {refTypeMode === 'custom' && (
                    <input
                      type="text"
                      className="form-control"
                      placeholder=""
                      value={customReferenceType}
                      onChange={(e) => {
                        setCustomReferenceType(e.target.value);
                        setPlaceReferenceType(e.target.value);
                      }}
                      style={{ height: '50px' }}
                    />
                  )}
                </div>

              </div>

              <div className="row mb-4">
                <div className="col-md-6 mb-3">
                  <label className="fw-semibold mb-2">Job Type</label>
                  <CustomDropdown
                    value={jobType}
                    options={config?.jobTypes && config.jobTypes.length > 0
                      ? config.jobTypes.map((jt) => ({ id: jt.code, name: jt.label }))
                      : [{ id: 1, name: '1' }]
                    }
                    onChange={(option) => setJobType(Number(option.id))}
                    placeholder="Select Job Type"
                    containerClassName="w-100"
                    buttonClassName="form-control w-100 d-flex align-items-center justify-content-between text-start bg-white text-dark border rounded-3"
                    buttonStyle={{ height: '50px' }}
                  />
                </div>

                <div className="col-md-6 mb-3">
                  <label className="fw-semibold mb-2">Form Name</label>
                  <CustomDropdown
                    value={jobMapping.formId}
                    options={(config?.forms || []).map((f) => ({ id: f.id, name: f.name }))}
                    onChange={(option) => setJobMapping({ ...jobMapping, formId: String(option.id) })}
                    placeholder="-- Select Form --"
                    containerClassName="w-100"
                    buttonClassName="form-control w-100 d-flex align-items-center justify-content-between text-start bg-white text-dark border rounded-3"
                    buttonStyle={{ height: '50px' }}
                  />
                </div>

              </div>

              <div className="row mb-4">
                <div className="col-md-6 mb-3">
                  <label className="fw-semibold mb-2">Upload File (.xlsx)</label>
                  <input
                    type="file"
                    accept=".xlsx, .xls"
                    onChange={handleFileChange}
                    className="form-control"
                    disabled={isFileParsing}
                    style={{ height: '50px' }}
                  />
                  {isFileParsing ? (
                    <div className="mt-2 text-primary fw-semibold small d-flex align-items-center">
                      <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                      Membaca dan memproses file Excel...
                    </div>
                  ) : selectedFile ? (
                    <div className="mt-2 text-primary fw-semibold small">
                      Selected file: {selectedFile.name} ({(selectedFile.size / 1024).toFixed(1)} KB, ~{excelRowCount} rows)
                    </div>
                  ) : null}
                </div>

                <div className="col-md-6 mb-3">
                  <label className="fw-semibold mb-2">Request Options</label>
                  <div className="row g-3 pt-1">
                    <div className="col-6">
                      <div className="form-check form-switch">
                        <input
                          className="form-check-input"
                          type="checkbox"
                          id="lockDistance"
                          checked={lockSubmitDistance}
                          onChange={(e) => setLockSubmitDistance(e.target.checked)}
                        />
                        <label className="form-check-label fw-semibold" htmlFor="lockDistance">
                          Lock Submit Distance
                        </label>
                      </div>
                    </div>

                    <div className="col-6">
                      <div className="form-check form-switch">
                        <input
                          className="form-check-input"
                          type="checkbox"
                          id="requireAudio"
                          checked={requireAudioRecording}
                          onChange={(e) => setRequireAudioRecording(e.target.checked)}
                        />
                        <label className="form-check-label fw-semibold" htmlFor="requireAudio">
                          Require Audio Recording
                        </label>
                      </div>
                    </div>

                    <div className="col-6">
                      <div className="form-check form-switch">
                        <input
                          className="form-check-input"
                          type="checkbox"
                          id="webFormEligible"
                          checked={webFormEligible}
                          onChange={(e) => setWebFormEligible(e.target.checked)}
                        />
                        <label className="form-check-label fw-semibold" htmlFor="webFormEligible">
                          Web Form Eligible
                        </label>
                      </div>
                    </div>

                    <div className="col-6">
                      <div className="form-check form-switch">
                        <input
                          className="form-check-input"
                          type="checkbox"
                          id="editableLocation"
                          checked={editableLocation}
                          onChange={(e) => setEditableLocation(e.target.checked)}
                        />
                        <label className="form-check-label fw-semibold" htmlFor="editableLocation">
                          Editable Location
                        </label>
                      </div>
                    </div>
                  </div>
                </div>
              </div>


              <div className="d-flex justify-content-end gap-2 mt-4">
                <button
                  className="btn btn-primary"
                  onClick={handleStep1Next}
                  disabled={!selectedFile || isFileParsing}
                >
                  {isFileParsing ? (
                    <span className="d-flex align-items-center">
                      <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                      Reading File...
                    </span>
                  ) : (
                    'Next'
                  )}
                </button>
              </div>

            </>
          )}

          {/* STEP 2: Column Mapping */}
          {step === 2 && (
            <>
              <h5 className="fw-bold mb-4">Column Mapping Schema</h5>
              <div className="row mb-4">
                {/* Job Data Mapping */}
                <div className="col-md-6 mb-4">
                  <div className="p-3 border rounded">
                    <p className="fw-bold text-primary mb-3">Job Json Mapping</p>

                    <div className="mb-3">
                      <label className="fw-semibold mb-1">
                        Name <span className="text-danger">*</span>
                      </label>
                      <div className="input-group">
                        <input
                          type="text"
                          className="form-control"
                          placeholder="Prefix (e.g. Survey - )"
                          value={jobPrefixName}
                          onChange={(e) => setJobPrefixName(e.target.value)}
                          style={{ maxWidth: '200px' }}
                        />
                        <CustomDropdown
                          value={jobMapping.name}
                          options={excelHeaders.map((h) => ({ id: h, name: h }))}
                          onChange={(option) => setJobMapping({ ...jobMapping, name: String(option.id) })}
                          placeholder="-- Select Column --"
                          containerClassName="flex-grow-1"
                          buttonClassName="form-control w-100 d-flex align-items-center justify-content-between text-start bg-white text-dark border rounded-3"
                        />
                      </div>
                      {jobPrefixName && jobMapping.name && (
                        <div className="mt-1 small text-muted">
                          Payload JSON: <span className="fw-semibold text-primary">"prefix": "{jobPrefixName}", "name": "{jobMapping.name}"</span>
                        </div>
                      )}

                    </div>


                    <div className="mb-3">
                      <label className="fw-semibold mb-1">Description (Opsional)</label>
                      <CustomDropdown
                        value={jobMapping.description}
                        options={excelHeaders.map((h) => ({ id: h, name: h }))}
                        onChange={(option) => setJobMapping({ ...jobMapping, description: option.id != null ? String(option.id) : '' })}
                        showDefaultOption={true}
                        defaultOptionLabel="-- Select (Opsional) --"
                        containerClassName="w-100"
                        buttonClassName="form-control w-100 d-flex align-items-center justify-content-between text-start bg-white text-dark border rounded-3"
                      />
                    </div>

                    <div className="mb-3">
                      <div className="d-flex justify-content-between align-items-center mb-1">
                        <label className="fw-semibold m-0">Max Input</label>
                        <div className="form-check">
                          <input
                            className="form-check-input"
                            type="checkbox"
                            id="useDefaultMaxInput"
                            checked={useDefaultMaxInput}
                            onChange={(e) => {
                              const checked = e.target.checked;
                              setUseDefaultMaxInput(checked);
                              setJobMapping((prev) => ({
                                ...prev,
                                maxInput: checked ? '1' : ''
                              }));
                            }}
                          />
                          <label className="form-check-label small text-muted ms-1" htmlFor="useDefaultMaxInput">
                            Gunakan Default (1)
                          </label>
                        </div>
                      </div>
                      {!useDefaultMaxInput ? (
                        <CustomDropdown
                          value={jobMapping.maxInput}
                          options={excelHeaders.map((h) => ({ id: h, name: h }))}
                          onChange={(option) => setJobMapping({ ...jobMapping, maxInput: option.id != null ? String(option.id) : '' })}
                          showDefaultOption={true}
                          defaultOptionLabel="-- Select (Opsional) --"
                          containerClassName="w-100"
                          buttonClassName="form-control w-100 d-flex align-items-center justify-content-between text-start bg-white text-dark border rounded-3"
                        />
                      ) : (
                        <input
                          type="text"
                          className="form-control bg-light text-muted"
                          value="1 (Default)"
                          readOnly
                        />
                      )}
                    </div>

                    <div className="mb-3">
                      <label className="fw-semibold mb-1">Group Name (Opsional)</label>
                      <CustomDropdown
                        value={jobMapping.groupName}
                        options={excelHeaders.map((h) => ({ id: h, name: h }))}
                        onChange={(option) => setJobMapping({ ...jobMapping, groupName: option.id != null ? String(option.id) : '' })}
                        showDefaultOption={true}
                        defaultOptionLabel="-- Select (Opsional) --"
                        containerClassName="w-100"
                        buttonClassName="form-control w-100 d-flex align-items-center justify-content-between text-start bg-white text-dark border rounded-3"
                      />
                    </div>

                    <div className="mb-3">
                      <div className="d-flex justify-content-between align-items-center mb-1">
                        <label className="fw-semibold m-0">Additional Info (Opsional)</label>
                        {jobMapping.additionalInfo.length > 0 && (
                          <span className="badge bg-secondary">
                            {jobMapping.additionalInfo.length} dipilih
                          </span>
                        )}
                      </div>

                      <div className="border rounded p-2" style={{ maxHeight: '180px', overflowY: 'auto' }}>
                        {excelHeaders.map((h) => {
                          const orderIndex = jobMapping.additionalInfo.indexOf(h);
                          const isChecked = orderIndex !== -1;
                          return (
                            <div key={h} className="form-check d-flex align-items-center justify-content-between py-1 border-bottom border-light">
                              <div>
                                <input
                                  className="form-check-input me-2"
                                  type="checkbox"
                                  id={`addInfo-${h}`}
                                  checked={isChecked}
                                  onChange={() => handleAdditionalInfoToggle(h)}
                                />
                                <label className="form-check-label small mb-0 cursor-pointer" htmlFor={`addInfo-${h}`}>
                                  {h}
                                </label>
                              </div>
                              {isChecked && (
                                <span className="badge rounded-pill bg-primary" title={`Urutan ke-${orderIndex + 1}`}>
                                  #{orderIndex + 1}
                                </span>
                              )}
                            </div>
                          );
                        })}
                      </div>

                      {jobMapping.additionalInfo.length > 0 && (
                        <div className="mt-2 small text-muted">
                          Urutan: {jobMapping.additionalInfo.map((item, idx) => (
                            <span key={item} className="badge bg-light text-dark border me-1 mb-1">
                              {idx + 1}. {item}
                            </span>
                          ))}
                        </div>
                      )}
                    </div>

                  </div>
                </div>

                {/* Place Data Mapping */}
                <div className="col-md-6 mb-4">
                  <div className="p-3 border rounded">
                    <p className="fw-bold text-primary mb-3">Place Json Mapping</p>


                    <div className="mb-3">
                      <label className="fw-semibold mb-1">
                        Reference ID <span className="text-danger">*</span>
                      </label>
                      <CustomDropdown
                        value={placeMapping.referenceId}
                        options={excelHeaders.map((h) => ({ id: h, name: h }))}
                        onChange={(option) => setPlaceMapping({ ...placeMapping, referenceId: String(option.id) })}
                        placeholder="-- Select Column --"
                        containerClassName="w-100"
                        buttonClassName="form-control w-100 d-flex align-items-center justify-content-between text-start bg-white text-dark border rounded-3"
                      />
                    </div>

                    <div className="mb-3">
                      <label className="fw-semibold mb-1">
                        Name <span className="text-danger">*</span>
                      </label>
                      <CustomDropdown
                        value={placeMapping.name}
                        options={excelHeaders.map((h) => ({ id: h, name: h }))}
                        onChange={(option) => setPlaceMapping({ ...placeMapping, name: String(option.id) })}
                        placeholder="-- Select Column --"
                        containerClassName="w-100"
                        buttonClassName="form-control w-100 d-flex align-items-center justify-content-between text-start bg-white text-dark border rounded-3"
                      />
                    </div>


                    <div className="mb-3">
                      <label className="fw-semibold mb-1">Address (Opsional)</label>
                      <CustomDropdown
                        value={placeMapping.address}
                        options={excelHeaders.map((h) => ({ id: h, name: h }))}
                        onChange={(option) => setPlaceMapping({ ...placeMapping, address: option.id != null ? String(option.id) : '' })}
                        showDefaultOption={true}
                        defaultOptionLabel="-- Select (Opsional) --"
                        containerClassName="w-100"
                        buttonClassName="form-control w-100 d-flex align-items-center justify-content-between text-start bg-white text-dark border rounded-3"
                      />
                    </div>

                    <div className="row g-2 mb-3">
                      <div className="col-6">
                        <label className="fw-semibold mb-1 small">Phone (Opsional)</label>
                        <CustomDropdown
                          value={placeMapping.phone}
                          options={excelHeaders.map((h) => ({ id: h, name: h }))}
                          onChange={(option) => setPlaceMapping({ ...placeMapping, phone: option.id != null ? String(option.id) : '' })}
                          showDefaultOption={true}
                          defaultOptionLabel="-- Select (Opsional) --"
                          containerClassName="w-100"
                          buttonClassName="form-control w-100 d-flex align-items-center justify-content-between text-start bg-white text-dark border rounded-3"
                        />
                      </div>
                      <div className="col-6">
                        <label className="fw-semibold mb-1 small">Postal Code (Opsional)</label>
                        <CustomDropdown
                          value={placeMapping.code}
                          options={excelHeaders.map((h) => ({ id: h, name: h }))}
                          onChange={(option) => setPlaceMapping({ ...placeMapping, code: option.id != null ? String(option.id) : '' })}
                          showDefaultOption={true}
                          defaultOptionLabel="-- Select (Opsional) --"
                          containerClassName="w-100"
                          buttonClassName="form-control w-100 d-flex align-items-center justify-content-between text-start bg-white text-dark border rounded-3"
                        />
                      </div>
                    </div>

                    <div className="row g-2 mb-3">
                      <div className="col-6">
                        <label className="fw-semibold mb-1 small">Latitude (Opsional)</label>
                        <CustomDropdown
                          value={placeMapping.latitude}
                          options={excelHeaders.map((h) => ({ id: h, name: h }))}
                          onChange={(option) => setPlaceMapping({ ...placeMapping, latitude: option.id != null ? String(option.id) : '' })}
                          showDefaultOption={true}
                          defaultOptionLabel="-- Select (Opsional) --"
                          containerClassName="w-100"
                          buttonClassName="form-control w-100 d-flex align-items-center justify-content-between text-start bg-white text-dark border rounded-3"
                        />
                      </div>
                      <div className="col-6">
                        <label className="fw-semibold mb-1 small">Longitude (Opsional)</label>
                        <CustomDropdown
                          value={placeMapping.longitude}
                          options={excelHeaders.map((h) => ({ id: h, name: h }))}
                          onChange={(option) => setPlaceMapping({ ...placeMapping, longitude: option.id != null ? String(option.id) : '' })}
                          showDefaultOption={true}
                          defaultOptionLabel="-- Select (Opsional) --"
                          containerClassName="w-100"
                          buttonClassName="form-control w-100 d-flex align-items-center justify-content-between text-start bg-white text-dark border rounded-3"
                        />
                      </div>
                    </div>

                    <div className="mb-3">
                      <label className="fw-semibold mb-1">Contact Person / PIC (Opsional)</label>
                      <CustomDropdown
                        value={placeMapping.contactPerson}
                        options={excelHeaders.map((h) => ({ id: h, name: h }))}
                        onChange={(option) => setPlaceMapping({ ...placeMapping, contactPerson: option.id != null ? String(option.id) : '' })}
                        showDefaultOption={true}
                        defaultOptionLabel="-- Select (Opsional) --"
                        containerClassName="w-100"
                        buttonClassName="form-control w-100 d-flex align-items-center justify-content-between text-start bg-white text-dark border rounded-3"
                      />
                    </div>

                  </div>
                </div>
              </div>

              <div className="d-flex justify-content-end gap-2 mt-4">
                <button className="btn btn-outline-primary" onClick={() => setStep(1)}>
                  Back
                </button>
                <button
                  className="btn btn-primary"
                  onClick={handleInquirySubmit}
                  disabled={isInquiryLoading || !jobMapping.name || !placeColumn}
                >
                  {isInquiryLoading ? (
                    <span className="d-flex align-items-center">
                      <span className="spinner-border spinner-border-sm me-2"></span>
                      Processing Inquiry...
                    </span>
                  ) : (
                    'Proceed to Preview'
                  )}
                </button>
              </div>
            </>
          )}

          {/* STEP 3: Preview & Execute */}
          {step === 3 && (
            <>
              {isExecutedSuccess ? (
                <div className="text-center py-5">
                  <i className="fa-solid fa-circle-check text-primary" style={{ fontSize: '8rem' }}></i>
                  <h5 className="text-primary mt-5">Job Creation Success</h5>
                  <p className="text-secondary mt-2">
                    Bulky jobs and places have been created successfully.
                  </p>
                  <button className="btn btn-primary mt-3" onClick={() => navigate(`/project/detail/${id}`)}>
                    Back to Project Job
                  </button>
                </div>
              ) : (
                <>
                  {/* Summary Metric Counters */}
                  {inquiryResult && (
                    <div className="row g-3 mb-4">
                      <div className="col-md">
                        <div className="p-3 border rounded text-center bg-light">
                          <div className="text-muted small">Total Rows</div>
                          <div className="fs-4 fw-bold">{inquiryResult.totalRows}</div>
                        </div>
                      </div>
                      <div className="col-md">
                        <div className="p-3 border rounded text-center bg-light">
                          <div className="text-success small fw-semibold">Valid Rows</div>
                          <div className="fs-4 fw-bold text-success">{inquiryResult.validRows}</div>
                        </div>
                      </div>
                      <div className="col-md">
                        <div className="p-3 border rounded text-center bg-light">
                          <div className="text-danger small fw-semibold">Invalid Rows</div>
                          <div className="fs-4 fw-bold text-danger">{inquiryResult.invalidRows}</div>
                        </div>
                      </div>
                      <div className="col-md">
                        <div className="p-3 border rounded text-center bg-light">
                          <div className="text-primary small fw-semibold">Existing Places</div>
                          <div className="fs-4 fw-bold text-primary">{inquiryResult.existingPlaces}</div>
                        </div>
                      </div>
                      <div className="col-md">
                        <div className="p-3 border rounded text-center bg-light">
                          <div className="text-warning small fw-semibold">New Places</div>
                          <div className="fs-4 fw-bold text-warning">{inquiryResult.newPlaces}</div>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Search and Nav Filter Bar */}
                  <div className="d-flex align-items-center mb-4 flex-wrap gap-2">
                    <form
                      onSubmit={(e) => {
                        e.preventDefault();
                        setCurrentPage(1);
                      }}
                      className="d-flex align-items-center me-3"
                      style={{ height: '50px' }}
                    >
                      <input
                        type="text"
                        className="form-control me-2"
                        placeholder="Search job, form, location..."
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        style={{ width: '350px', height: '50px' }}
                      />
                      <button
                        type="submit"
                        className="btn btn-primary"
                        style={{ height: '50px' }}
                        onClick={() => setCurrentPage(1)}
                      >
                        <i className="fa-solid fa-search"></i>
                      </button>
                    </form>

                    <div className="d-flex justify-content-end ms-auto" style={{ flex: 1 }}>
                      <div ref={filterNavRef} className="nav-pills-animated h-100">
                        <div className="nav-pill-indicator" style={filterIndicatorStyle} />
                        <button
                          type="button"
                          className={`nav-link fw-semibold ${filter === 'all' ? 'active' : ''}`}
                          onClick={() => setFilter('all')}
                        >
                          All
                        </button>
                        <button
                          type="button"
                          className={`nav-link fw-semibold ${filter === 'valid' ? 'active' : ''}`}
                          onClick={() => setFilter('valid')}
                        >
                          Valid
                        </button>
                        <button
                          type="button"
                          className={`nav-link fw-semibold ${filter === 'invalid' ? 'active' : ''}`}
                          onClick={() => setFilter('invalid')}
                        >
                          Not Valid
                        </button>
                      </div>
                    </div>
                  </div>

                  {/* Table (styled identical to Advance Job Assignment) */}
                  <table className="table table-condensed table-hover">
                    <thead>
                      <tr>
                        <th style={{ width: '60px' }} className="text-center">Row</th>
                        <th className="text-start">Job Name</th>
                        <th className="text-center">Form Name</th>
                        <th className="text-center">Group</th>
                        <th className="text-center">Place Status</th>
                        <th className="text-center">Location Identifier</th>
                        <th className="text-center">Check Valid</th>
                      </tr>
                    </thead>
                    <tbody>
                      {currentRows.length > 0 ? (
                        currentRows.map((row) => (
                          <tr key={row.rowNumber}>
                            <td className="text-center fw-bold">{row.rowNumber}</td>
                            <td className="text-start">
                              <div className="fw-semibold">{row.jobName}</div>
                              {row.jobDescription && (
                                <div className="small text-muted">{row.jobDescription}</div>
                              )}
                            </td>
                            <td className="text-center">{row.formName}</td>
                            <td className="text-center">{row.groupName || '-'}</td>
                            <td className="text-center">
                              <span
                                className={`badge ${row.placeStatus === 'existing'
                                  ? 'bg-info'
                                  : row.placeStatus === 'new'
                                    ? 'bg-warning text-dark'
                                    : 'bg-secondary'
                                  }`}
                              >
                                {row.placeStatus}
                              </span>
                            </td>
                            <td className="text-center">
                              <div>{row.locationIdentifier || '-'}</div>
                              {row.newPlaceDetails && (
                                <div className="small text-muted">
                                  {row.newPlaceDetails.name}
                                </div>
                              )}
                            </td>
                            <td className="text-center">
                              {row.isValid ? (
                                <span className="text-success fw-semibold">valid</span>
                              ) : (
                                <span className="d-inline-flex align-items-center text-danger fw-semibold">
                                  not valid&nbsp;
                                  <div
                                    className="tooltip-trigger d-inline-flex align-items-center position-relative"
                                    onMouseEnter={(e) => {
                                      const rect = e.currentTarget.getBoundingClientRect();
                                      const tooltip = document.createElement('div');
                                      tooltip.className = 'global-tooltip';
                                      tooltip.innerText = row.reason || 'Unknown reason';
                                      document.body.appendChild(tooltip);

                                      const top = rect.top + window.scrollY - tooltip.offsetHeight - 8;
                                      const left = rect.left + window.scrollX + rect.width / 2 - tooltip.offsetWidth / 2;
                                      tooltip.style.top = `${top}px`;
                                      tooltip.style.left = `${left}px`;
                                    }}
                                    onMouseLeave={() => {
                                      document.querySelectorAll('.global-tooltip').forEach((el) => el.remove());
                                    }}
                                  >
                                    <i className="fa-solid fa-circle-info text-dark" style={{ cursor: 'pointer' }}></i>
                                  </div>
                                </span>
                              )}
                            </td>
                          </tr>
                        ))
                      ) : (
                        <tr>
                          <td colSpan={7} className="text-center text-muted py-4">
                            No data found
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>

                  {/* Pagination Footer (Identical to Advance Job Assignment) */}
                  <div className="d-flex justify-content-end align-items-center mt-3">
                    <div className="d-flex align-items-center">
                      <div className="me-4 d-flex align-items-center">
                        <label className="me-2 mb-0">Rows per page:</label>
                        <CustomDropdown
                          value={rowsPerPage}
                          options={[10, 25, 50, 100].map((size) => ({ id: size, name: String(size) }))}
                          onChange={(option) => {
                            setRowsPerPage(Number(option.id));
                            setCurrentPage(1);
                          }}
                          placeholder="Rows"
                          buttonClassName="btn btn-outline-secondary d-flex align-items-center justify-content-between gap-2 px-3 py-2 text-dark bg-white border rounded-3"
                        />
                      </div>

                      <span className="me-3">
                        {`${filteredRows.length === 0 ? 0 : indexOfFirstRow + 1}–${Math.min(
                          indexOfLastRow,
                          filteredRows.length
                        )} of ${filteredRows.length}`}
                      </span>

                      <nav>
                        <ul className="pagination mb-0">
                          <li className={`page-item ${currentPage === 1 ? 'disabled' : ''}`}>
                            <button
                              className="page-link"
                              onClick={() => setCurrentPage(1)}
                              style={{ border: 0 }}
                            >
                              <i className="fa fa-angle-double-left"></i>
                            </button>
                          </li>
                          <li className={`page-item ${currentPage === 1 ? 'disabled' : ''}`}>
                            <button
                              className="page-link"
                              onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                              style={{ border: 0 }}
                            >
                              <i className="fa fa-angle-left"></i>
                            </button>
                          </li>
                          <li className="page-item disabled">
                            <span className="page-link" style={{ border: 0 }}>
                              {currentPage}
                            </span>
                          </li>
                          <li className={`page-item ${currentPage === totalPages ? 'disabled' : ''}`}>
                            <button
                              className="page-link"
                              onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
                              style={{ border: 0 }}
                            >
                              <i className="fa fa-angle-right"></i>
                            </button>
                          </li>
                          <li className={`page-item ${currentPage === totalPages ? 'disabled' : ''}`}>
                            <button
                              className="page-link"
                              onClick={() => setCurrentPage(totalPages)}
                              style={{ border: 0 }}
                            >
                              <i className="fa fa-angle-double-right"></i>
                            </button>
                          </li>
                        </ul>
                      </nav>
                    </div>
                  </div>

                  {/* Actions */}
                  <div className="d-flex justify-content-end gap-2 mt-4">
                    <button className="btn btn-outline-primary" onClick={() => setStep(2)}>
                      Back
                    </button>
                    <button
                      className="btn btn-primary"
                      onClick={handleExecuteSubmit}
                      disabled={isExecuting || (inquiryResult?.validRows || 0) === 0}
                    >
                      {isExecuting ? (
                        <span className="d-flex align-items-center">
                          <span className="spinner-border spinner-border-sm me-2"></span>
                          Executing...
                        </span>
                      ) : (
                        'Proceed'
                      )}
                    </button>
                  </div>
                </>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default ProjectJobCreation;
